import { LabelWithMessage } from '@common/LabelWithMessage';
import { useBreakPoints } from '@common/breakpoints.hooks';
import { FETCH_QUERIES_NAME } from '@common/constants';
import usePrompt from '@common/prompt.hooks';
import { Attachments } from '@file/Attachments';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { IProjectFileAttachmentsTransmittalsRequest } from '@file/projectFile/projectFile.models';
import { useFetchAttachmentTransmittal } from '@file/projectFile/projectFile.queries';
import { CheckboxType, Form, FormCheckboxGroup, FormTextarea, Grid, useForm } from '@gcl/react-core';
import { IProjectLambethReviewerResponse } from '@transmittal/transmittal.models';
import { DiscussionList } from '@discussion/DiscussionList';
import { EProjectDiscussionFormType } from '@discussion/projectDiscussion/projectDiscussion.constants';
import { EProjectDiscussionFileType } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';
import { ETransmittalReviewResponseFormKey, reviewResponseCheckOptions } from './transmittalDetailPage.constants';

interface ITransmittalDetailTabReviewResponseProps {
  lambethReviewerResponse: IProjectLambethReviewerResponse;
}

export const TransmittalDetailTabReviewResponse = ({
  lambethReviewerResponse,
}: ITransmittalDetailTabReviewResponseProps) => {
  const { isXs, isSm } = useBreakPoints();

  const isReadOnly = true;

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  return (
    <Form
      defaultValues={{
        callback: () => {
          const option = reviewResponseCheckOptions.find(
            (checkOption) => checkOption.data === lambethReviewerResponse.status,
          );
          const state = {
            [ETransmittalReviewResponseFormKey.COMMENT]: lambethReviewerResponse.remark ?? null,
            [ETransmittalReviewResponseFormKey.REVIEW_RESPONSE]: option ? [option] : [],
          };
          return state;
        },
        deps: [lambethReviewerResponse],
      }}
      enableUnsavedChangesPrompt
      unsavedChangesPromptProps={{
        usePrompt,
      }}
      useFormMethods={useFormMethods}
      render={(formFieldMethods) => {
        return (
          <>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12}>
                <LabelWithMessage labelFor={ETransmittalReviewResponseFormKey.REVIEW_RESPONSE} text="Review Response" />
                <FormCheckboxGroup
                  type={CheckboxType.Radio}
                  itemPerRow={isSm || isXs ? 1 : 3}
                  checkOptions={reviewResponseCheckOptions}
                  id={ETransmittalReviewResponseFormKey.REVIEW_RESPONSE}
                  formFieldMethods={useFormMethods}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <LabelWithMessage
                  labelFor={ETransmittalReviewResponseFormKey.DESIGNER_ATTACHMENTS}
                  text="Designer's Attachments"
                />
                <Attachments<IProjectFileAttachmentsTransmittalsRequest>
                  id={`supplementary-info-${EProjectFileType.LAMBETH_REVIEWER_RESPONSE_ATTACHMENT}`}
                  useFetchFilesQueryKey={[
                    lambethReviewerResponse.projectId,
                    lambethReviewerResponse.transmittalId,
                    FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_TRANSMITTALS,
                    EProjectFileType.LAMBETH_REVIEWER_RESPONSE_ATTACHMENT,
                  ]}
                  fileType={EProjectFileType.LAMBETH_REVIEWER_RESPONSE_ATTACHMENT}
                  mode={isReadOnly ? 'View' : 'Update'}
                  useFetchFiles={useFetchAttachmentTransmittal}
                  useFetchFilesProps={{
                    transmittalId: lambethReviewerResponse.transmittalId,
                    fileType: EProjectFileType.LAMBETH_REVIEWER_RESPONSE_ATTACHMENT,
                    projectId: lambethReviewerResponse.projectId,
                  }}
                  uploadFileRequestParam={{ transmittalId: lambethReviewerResponse.projectId }}
                />
              </Grid>
              <Grid item xs={12}>
                <LabelWithMessage labelFor={ETransmittalReviewResponseFormKey.COMMENT} text="Comment" />
                <FormTextarea
                  id={ETransmittalReviewResponseFormKey.COMMENT}
                  formFieldMethods={useFormMethods}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
            <DiscussionList
              projectLambethReviewerResponseId={lambethReviewerResponse.id}
              formType={EProjectDiscussionFormType.LAMBETH_REVIEWER_RESPONSE}
              fileFormType={EProjectFileType.LAMBETH_REVIEWER_RESPONSE_DISCUSSION_ATTACHMENT}
              discussionFileType={EProjectDiscussionFileType.LAMBETH_REVIEWER_RESPONSE}
              canComment={false}
              canResponse={false}
              canViewDraftComment
              isReadOnly
              showNoneWhenNoRecordInReadOnly
              title="Discussion"
            />
          </>
        );
      }}
    />
  );
};
